import type { FC, MouseEvent, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import Link from 'next/link'
import { useRef, useState } from 'react'
import { AnimatePresence, motion, useMotionValue, useSpring } from 'motion/react'
import { duration, easeOut } from '@/lib/motion'
import { Reveal } from '@/components/motion/reveal'
import { SectionHeading } from '@/components/site/section-heading'

interface HomeIndexProps {
    projects: ProjectSummary[]
}

const PREVIEW_WIDTH = 280
const PREVIEW_HEIGHT = PREVIEW_WIDTH / 1.41562

export const HomeIndex: FC<HomeIndexProps> = ({ projects }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const [hovered, setHovered] = useState<string | null>(null)

    const pointerX = useMotionValue(0)
    const pointerY = useMotionValue(0)
    const x = useSpring(pointerX, { stiffness: 260, damping: 30, mass: 0.6 })
    const y = useSpring(pointerY, { stiffness: 260, damping: 30, mass: 0.6 })

    const handleMove = (event: MouseEvent<HTMLElement>) => {
        const bounds = sectionRef.current?.getBoundingClientRect()

        if (!bounds) return

        const nextX = event.clientX - bounds.left - PREVIEW_WIDTH / 2
        const nextY = event.clientY - bounds.top - PREVIEW_HEIGHT / 2

        pointerX.set(nextX)
        pointerY.set(nextY)

        if (!hovered) {
            x.jump(nextX)
            y.jump(nextY)
        }
    }

    const current = projects.find((project) => project.id === hovered)

    if (projects.length === 0) return null

    return (
        <section
            ref={sectionRef}
            id='project-index'
            onMouseMove={handleMove}
            className='relative z-[2] flex w-full flex-col gap-10 overflow-clip bg-background px-4 py-section-sm lg:px-6'
        >
            <div className='flex w-full flex-col md:flex-row md:items-end md:justify-between md:gap-6'>
                <div className='w-full md:flex-1'>
                    <SectionHeading lead="The full" title="index" />
                </div>

                <p className='t-body max-w-[440px] pb-6 text-left text-muted-foreground md:max-w-[32%]'>
                    Every project I have worked on so far, from client websites to my thesis. Hover over a name to see a glimpse, or open it to read the whole story.
                </p>
            </div>

            <ul
                onMouseLeave={() => setHovered(null)}
                className='flex w-full flex-col border-t border-border'
            >
                {projects.map((project, index) => (
                    <li key={project.id} className='border-b border-border'>
                        <Reveal delay={index * 0.05} y={16}>
                            <Link
                                href={`/work/${project.slug}`}
                                onMouseEnter={() => setHovered(project.id)}
                                onFocus={() => setHovered(project.id)}
                                onBlur={() => setHovered(null)}
                                className='group/row grid grid-cols-[minmax(0,1fr)_auto] items-end gap-4 py-5 text-foreground md:grid-cols-[minmax(0,1fr)_minmax(0,0.55fr)_auto] md:gap-6 lg:py-6'
                            >
                                <span className='flex items-baseline gap-3 transition-transform duration-fast ease-studio group-hover/row:translate-x-2 group-focus-visible/row:translate-x-2'>
                                    <span className='t-number shrink-0 text-muted-foreground'>
                                        {String(index + 1).padStart(2, '0')}
                                    </span>

                                    <span
                                        data-mdlfy-cms={`projects:${project.id}:client`}
                                        className='t-name text-left max-md:text-[44px]'
                                    >
                                        {project.client}
                                    </span>
                                </span>

                                <span className='hidden items-baseline gap-3 pb-1 md:flex'>
                                    <span
                                        data-mdlfy-cms={`projects:${project.id}:title`}
                                        className='t-italic text-left'
                                    >
                                        {project.title}
                                    </span>

                                    {project.category && (
                                        <span
                                            data-mdlfy-cms={`categories:${project.category.id}:name`}
                                            className='t-caps text-left text-muted-foreground'
                                        >
                                            {project.category.name}
                                        </span>
                                    )}
                                </span>

                                <span
                                    data-mdlfy-cms={`projects:${project.id}:year`}
                                    className='t-italic whitespace-nowrap pb-1 text-right'
                                >
                                    {project.year}
                                </span>
                            </Link>
                        </Reveal>
                    </li>
                ))}
            </ul>

            <motion.div
                aria-hidden='true'
                style={{ x, y, width: PREVIEW_WIDTH, height: PREVIEW_HEIGHT }}
                className='pointer-events-none absolute left-0 top-0 z-[3] hidden lg:block'
            >
                <AnimatePresence>
                    {current?.cover && (
                        <motion.img
                            key={current.id}
                            src={current.cover}
                            alt=''
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.98 }}
                            transition={{ duration: duration.base, ease: easeOut }}
                            className='absolute inset-0 size-full object-cover'
                        />
                    )}
                </AnimatePresence>
            </motion.div>
        </section>
    )
}
